import { SettingOutlined, CloseOutlined } from "@ant-design/icons";
import { IconButton } from "@/components/UI/TableToolbar/IconButton";
import { isSingleSubjectPage } from "@/utils/workspaceUtils";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useCallback, useState } from "react";
import { Popover, Button } from "antd";
import { TableState, ColumnsType } from "@/types/tableState.types";
import { WorkspaceProps } from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import { tableStateToWorkspaceConfig } from "@/utils/workspaceState";
import { WorkspaceConfig } from "@/types/workspaceConfig";
import { ImportWorkspaceButton, ImportWorkspaceModal } from "./ImportWorkspace";
import { AdvancedColumnsDrawer } from "./AdvancedColumnsDrawer";

const saveJSON = (name: string, data: unknown) => {
  const dataStr =
    "data:text/json;charset=utf-8," +
    encodeURIComponent(JSON.stringify(data, null, 2));
  const downloadAnchorNode = document.createElement("a");
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", name);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

type SaveViewButtonProps = {
  currentTableState: TableState;
  pageName: string;
  allColumns: string[];
  fullPageName: string;
  columnsTitleCreator: (fieldName: string) => string;
  enableColumnStateEditing?: boolean;
  columnsConfig?: ColumnsType[];
  workspace?: WorkspaceProps;
};

export const SaveViewButton = ({
  currentTableState,
  pageName,
  fullPageName,
  allColumns,
  columnsTitleCreator,
  enableColumnStateEditing = false,
  columnsConfig,
  workspace,
}: SaveViewButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState(false);
  const [importedConfig, setImportedConfig] = useState<WorkspaceConfig | null>(
    null,
  );

  const handleImportComplete = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <AdvancedColumnsDrawer
        isOpen={isColumnDrawerOpen}
        setIsOpen={setIsColumnDrawerOpen}
        allColumns={allColumns}
        columnsTitleCreator={columnsTitleCreator}
        pageName={pageName}
        fullPageName={fullPageName}
        enableColumnStateEditing={enableColumnStateEditing}
        columnsConfig={columnsConfig}
      />

      {workspace && (
        <ImportWorkspaceModal
          workspace={workspace}
          importedConfig={importedConfig}
          onClose={() => setImportedConfig(null)}
          onImportComplete={handleImportComplete}
        />
      )}

      <div className="flex flex-col gap-2">
        <ConfigPopover
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          currentTableState={currentTableState}
          pageName={pageName}
          fullPageName={fullPageName}
          setColumnDrawerOpen={setIsColumnDrawerOpen}
          workspace={workspace}
          onConfigLoaded={setImportedConfig}
        />
      </div>
    </>
  );
};

type ConfigPopoverProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentTableState: TableState;
  pageName: string;
  fullPageName: string;
  setColumnDrawerOpen: (isOpen: boolean) => void;
  workspace?: WorkspaceProps;
  onConfigLoaded: (config: WorkspaceConfig) => void;
};

const ConfigPopover = ({
  isOpen,
  setIsOpen,
  currentTableState,
  fullPageName,
  setColumnDrawerOpen,
  workspace,
  onConfigLoaded,
}: ConfigPopoverProps) => {
  const { id } = useScopeParams();

  const isSSJ = isSingleSubjectPage(fullPageName);
  const excludeFilterKeys = isSSJ ? ["subject"] : [];

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
  };

  return (
    <Popover
      content={
        <div className="flex flex-col items-start justify-start w-full">
          <button
            className="w-full flex flex-row gap-2 py-1 items-center justify-start hover:bg-[#ECFAFF]"
            onClick={() => {
              const viewConfig = tableStateToWorkspaceConfig(
                currentTableState,
                excludeFilterKeys,
              );
              const trialIdentifier = id ?? "unknownTrial";
              saveJSON(
                `${trialIdentifier}-${fullPageName}-defaultView.json`,
                viewConfig,
              );
            }}
          >
            Export Workspace
          </button>

          {workspace && (
            <ImportWorkspaceButton onConfigLoaded={onConfigLoaded} />
          )}

          <button
            className="w-full flex flex-row gap-2 py-1 items-center justify-start hover:bg-[#ECFAFF]"
            onClick={() => {
              setColumnDrawerOpen(true);
            }}
          >
            Configure Columns
          </button>
        </div>
      }
      title={
        <div className="flex items-center justify-between pb-1 border-b">
          <p className="text-base font-semibold">Superuser Configuration</p>
          <Button
            type="text"
            onClick={() => setIsOpen(false)}
            icon={<CloseOutlined className="text-[#00000073]" />}
          />
        </div>
      }
      trigger="click"
      placement="bottomRight"
      arrow={false}
      open={isOpen}
      onOpenChange={handleOpenChange}
      autoAdjustOverflow={false}
      align={{ offset: [0, 10] }}
      destroyOnHidden={true}
    >
      <span>
        <IconButton
          title="Superuser Mode - Workspace Config"
          icon={<SettingOutlined className="text-md text-[16px] p-[2px]" />}
        />
      </span>
    </Popover>
  );
};
